import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { useScheduledJob } from '@cast/utils';

import { useGetNewlyOnboardedClusters } from '@oneclick/hooks/queries/cluster/useGetNewlyOnboardedClusters';

import { ConnectionChoice, CONNECTING_CHECK_STEPS } from '../../../_constants';
import {
  getActiveChecklistStep,
  isConnectionDataReady,
} from '../_utils/connectionChecklistProgress';
import { useBaselineIntegrationIds } from './useBaselineIntegrationIds';
import { useDiscoveredEnvironmentStats } from './useDiscoveredEnvironmentStats';

const CONNECTION_TIMEOUT_MS = 5 * 60 * 1000;

const EMPTY_STATS = {
  clusters: undefined as number | undefined,
  virtualMachines: undefined as number | undefined,
  volume: undefined as number | undefined,
  loadBalancers: undefined as number | undefined,
  commitments: undefined as number | undefined,
};

type UseOnboardingConnectionProgressArgs = {
  connectionChoice: ConnectionChoice;
  isConnecting: boolean;
  showResult: boolean;
  connectionStartedAt?: Date;
  captureBaseline: boolean;
};

export const useOnboardingConnectionProgress = ({
  connectionChoice,
  isConnecting,
  showResult,
  connectionStartedAt,
  captureBaseline,
}: UseOnboardingConnectionProgressArgs) => {
  const usesInventoryStats =
    connectionChoice === ConnectionChoice.cloudConnect ||
    connectionChoice === ConnectionChoice.snapshot;

  const baselineIntegrationIds = useBaselineIntegrationIds({
    shouldCapture: usesInventoryStats && (captureBaseline || isConnecting),
  });
  const hasBaseline = baselineIntegrationIds !== null;

  const {
    stats: inventoryStats,
    assetListClusters,
    newIntegration,
    syncStatuses,
    isLoading: isStatsLoading,
  } = useDiscoveredEnvironmentStats({
    polling: isConnecting && usesInventoryStats && hasBaseline,
    enabled: showResult && usesInventoryStats && hasBaseline,
    baselineIntegrationIds: baselineIntegrationIds ?? new Set(),
  });

  const { newConnections, refetchClusters } = useGetNewlyOnboardedClusters({
    polling: isConnecting && connectionChoice === ConnectionChoice.single,
    enabled: showResult && connectionChoice === ConnectionChoice.single,
    clusterScanTimeFrom: connectionStartedAt,
  });

  const [isTimedOut, setIsTimedOut] = useState(false);

  const { start, clear } = useScheduledJob();

  useEffect(() => {
    if (!isConnecting) {
      setIsTimedOut(false);
      clear();
      return;
    }

    start({
      when: dayjs().add(CONNECTION_TIMEOUT_MS, 'milliseconds'),
      job: () => setIsTimedOut(true),
    });

    return clear;
  }, [clear, connectionChoice, isConnecting, start]);

  const hasNewCluster = newConnections.length > 0;
  const hasIntegration = Boolean(newIntegration);
  const assetListClusterCount = assetListClusters?.length ?? 0;

  const isDataReady = useMemo(
    () =>
      isConnectionDataReady({
        connectionChoice,
        hasIntegration,
        syncStatuses,
        assetListClusterCount,
        hasNewCluster,
      }),
    [
      assetListClusterCount,
      connectionChoice,
      hasIntegration,
      hasNewCluster,
      syncStatuses,
    ]
  );

  const activeChecklistStep = useMemo(() => {
    if (!isConnecting && !showResult) {
      return 0;
    }

    if (isDataReady) {
      return CONNECTING_CHECK_STEPS.length;
    }

    return getActiveChecklistStep({
      connectionChoice,
      hasIntegration,
      syncStatuses,
      assetListClusterCount,
      hasNewCluster,
    });
  }, [
    assetListClusterCount,
    connectionChoice,
    hasIntegration,
    hasNewCluster,
    isConnecting,
    isDataReady,
    showResult,
    syncStatuses,
  ]);

  const isComplete = isConnecting && isDataReady;

  const stats = useMemo(() => {
    if (connectionChoice === ConnectionChoice.single) {
      return {
        ...EMPTY_STATS,
        clusters: hasNewCluster ? newConnections.length : undefined,
      };
    }

    return inventoryStats;
  }, [connectionChoice, hasNewCluster, inventoryStats, newConnections.length]);

  const isSummaryLoading =
    isConnecting &&
    !isComplete &&
    (usesInventoryStats ? isStatsLoading : !hasNewCluster);

  return {
    stats,
    activeChecklistStep,
    isComplete,
    isTimedOut,
    isSummaryLoading,
    refetchClusters,
    newConnections,
  };
};
